import React, { useState, useEffect } from "react";
import { adminApi } from "../services/api";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatPercentage } from "../utils";

const CHART_COLORS = [
  "#3b82f6", "#10b981", "#8b5cf6", "#ef4444", "#f59e0b", "#06b6d4",
  "#ec4899", "#84cc16", "#6366f1", "#f97316", "#14b8a6", "#a855f7",
  "#eab308", "#64748b",
];

const DashboardPage = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeConnections: 0,
    expiredConnections: 0,
    notConnected: 0,
  });

  // Graph data states
  const [userGrowthPeriod, setUserGrowthPeriod] = useState("weekly");
  const [userGrowthData, setUserGrowthData] = useState([]);

  const [apiUsagePeriod, setApiUsagePeriod] = useState("weekly");
  const [apiUsageData, setApiUsageData] = useState([]);

  const [apiCallsPeriod, setApiCallsPeriod] = useState("weekly");
  const [apiCallsData, setApiCallsData] = useState([]);

  const [errorSuccessRatioPeriod, setErrorSuccessRatioPeriod] = useState("weekly");
  const [errorSuccessRatioData, setErrorSuccessRatioData] = useState([]);

  const [entityStatsPeriod, setEntityStatsPeriod] = useState("weekly");
  const [entityStatsData, setEntityStatsData] = useState([]);

  const [moduleStatsPeriod, setModuleStatsPeriod] = useState("weekly");
  const [moduleStatsData, setModuleStatsData] = useState([]);
  const [moduleStatsLoading, setModuleStatsLoading] = useState(true);

  const [realmCallsPeriod, setRealmCallsPeriod] = useState("weekly");
  const [realmCallsRealmId, setRealmCallsRealmId] = useState("");
  const [realmCallsData, setRealmCallsData] = useState([]);
  const [realmIdOptions, setRealmIdOptions] = useState([]);

  useEffect(() => {
    fetchOverview();
    fetchUserStats();
    fetchRealmIdOptions();
  }, []);

  useEffect(() => {
    fetchUserGrowth();
  }, [userGrowthPeriod]);

  useEffect(() => {
    fetchApiUsage();
  }, [apiUsagePeriod]);

  useEffect(() => {
    fetchApiCalls();
  }, [apiCallsPeriod]);

  useEffect(() => {
    fetchErrorSuccessRatio();
  }, [errorSuccessRatioPeriod]);

  useEffect(() => {
    fetchEntityStats();
  }, [entityStatsPeriod]);

  useEffect(() => {
    fetchModuleStats();
  }, [moduleStatsPeriod]);

  useEffect(() => {
    fetchApiCallsByRealm();
  }, [realmCallsPeriod, realmCallsRealmId]);

  const fetchOverview = async () => {
    try {
      const response = await adminApi.getAnalyticsOverview();
      setOverview(response.data.overview);
    } catch (err) {
      console.error("Failed to fetch overview:", err);
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await adminApi.getUserStats();
      setUserStats(response.data.stats);
    } catch (err) {
      console.error("Failed to fetch user stats:", err);
    }
  };

  const fetchUserGrowth = async () => {
    try {
      const response = await adminApi.getUserGrowth(userGrowthPeriod);
      setUserGrowthData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch user growth:", err);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const response = await adminApi.getApiUsage(apiUsagePeriod);
      setApiUsageData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch API usage:", err);
    }
  };

  const fetchApiCalls = async () => {
    try {
      const response = await adminApi.getApiCalls(apiCallsPeriod);
      setApiCallsData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch API calls:", err);
    }
  };

  const fetchErrorSuccessRatio = async () => {
    try {
      const response = await adminApi.getApiUsage(errorSuccessRatioPeriod);
      setErrorSuccessRatioData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch error/success ratio:", err);
    }
  };

  const fetchEntityStats = async () => {
    try {
      console.log("fetchEntityStats request params:", { period: entityStatsPeriod });
      const response = await adminApi.getAuditLogStats({ period: entityStatsPeriod });
      console.log("fetchEntityStats response:", response.data);
      setEntityStatsData(response.data.stats.entityStats || []);
    } catch (err) {
      console.error("Failed to fetch entity stats:", err);
    }
  };

  const fetchModuleStats = async () => {
    setModuleStatsLoading(true);
    try {
      const response = await adminApi.getModuleStats({ period: moduleStatsPeriod });
      setModuleStatsData(response.data.moduleStats || []);
    } catch (err) {
      console.error("Failed to fetch module stats:", err);
    } finally {
      setModuleStatsLoading(false);
    }
  };

  const fetchRealmIdOptions = async () => {
    try {
      const response = await adminApi.getRealmIds();
      setRealmIdOptions(response.data.realmIds || []);
    } catch (err) {
      console.error("Failed to fetch realm ids:", err);
    }
  };

  const fetchApiCallsByRealm = async () => {
    try {
      const response = await adminApi.getApiCallsByRealm({
        period: realmCallsPeriod,
        ...(realmCallsRealmId ? { realmId: realmCallsRealmId } : {}),
      });
      setRealmCallsData(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch API calls by realm:", err);
    }
  };

  const PeriodSelector = ({ value, onChange }) => (
    <div className="flex border border-line rounded-lg overflow-hidden bg-white">
      {["weekly", "monthly", "yearly"].map((period) => (
        <button
          key={period}
          onClick={() => onChange(period)}
          className={`border-0 py-2 px-4 text-sm font-medium cursor-pointer border-r border-line last:border-r-0 transition-all ${
            value === period ? "bg-[#1e293b] text-white" : "text-muted hover:bg-soft "
          }`}
        >
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
        <div className="py-16 text-center">
          <div className="inline-block w-8 h-8 border-3 border-line border-t-indigo rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
        <div className="py-16 text-center text-red-600">{error}</div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="mb-4">
        <h1 className="text-[28px] font-bold tracking-tight m-0">Dashboard</h1>
        <p className="text-muted text-[15px] mt-1 mb-0">{getGreeting()}, Admin! Monitor API usage, user growth, and system performance metrics.</p>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total Users</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight">{userStats.totalUsers}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Active Connections</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-green-700">{userStats.activeConnections}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Expired Connections</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-red-700">{userStats.expiredConnections}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Not Connected</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight">{userStats.notConnected}</div>
        </div>
      </div>

      {/* State Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total API Calls</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight">{overview?.totalApiCalls || 0}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Failed API Calls</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-red-700">{overview?.totalFailedApiCalls || 0}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Error Rate</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-yellow-700">{formatPercentage(overview?.errorRate || 0)}%</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Success Rate</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-green-700">{formatPercentage(100 - (overview?.errorRate || 0))}%</div>
        </div>
      </div>

      {/* Graphs */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Users Growth */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Users Growth</h3>
            <PeriodSelector value={userGrowthPeriod} onChange={setUserGrowthPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#6366f1"
                  strokeWidth={2.5}
                  dot={{ fill: "#6366f1", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="New Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Usage */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Usage</h3>
            <PeriodSelector value={apiUsagePeriod} onChange={setApiUsagePeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={apiUsageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Bar dataKey="successCount" stackId="a" fill="#10b981" name="Success" radius={[0, 0, 0, 0]} />
                <Bar dataKey="errorCount" stackId="a" fill="#ef4444" name="Error" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Calls */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Calls</h3>
            <PeriodSelector value={apiCallsPeriod} onChange={setApiCallsPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={apiCallsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8b5cf6"
                  strokeWidth={2.5}
                  dot={{ fill: "#8b5cf6", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="API Calls"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Calls By Realm ID */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-lg font-bold text-ink m-0">API Calls By Realm ID</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={realmCallsRealmId}
                onChange={(e) => setRealmCallsRealmId(e.target.value)}
                className="py-2 px-3 border border-line rounded-lg bg-white cursor-pointer text-sm font-medium text-ink hover:bg-soft transition-all focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
              >
                <option value="">All Realm IDs</option>
                {realmIdOptions.map((realmId) => (
                  <option key={realmId} value={realmId}>
                    {realmId}
                  </option>
                ))}
              </select>
              <PeriodSelector value={realmCallsPeriod} onChange={setRealmCallsPeriod} />
            </div>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={realmCallsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#06b6d4"
                  strokeWidth={2.5}
                  dot={{ fill: "#06b6d4", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="API Calls"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Calls By Module */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Calls By Module</h3>
            <PeriodSelector value={entityStatsPeriod} onChange={setEntityStatsPeriod} />
          </div>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={entityStatsData} margin={{ bottom: 40, left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="name"
                  stroke="#6b7280"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Activity" minPointSize={2}>
                  {entityStatsData.map((entry, index) => (
                    <Cell key={entry.entity || index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Module Retrieval Counts */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Retrieval Counts By Module</h3>
            <PeriodSelector value={moduleStatsPeriod} onChange={setModuleStatsPeriod} />
          </div>
          {moduleStatsLoading ? (
            <div className="py-16 text-center">
              <div className="inline-block w-8 h-8 border-3 border-line border-t-indigo rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={moduleStatsData} margin={{ bottom: 40, left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "12px",
                      padding: "8px 12px",
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Retrieval Counts" minPointSize={2}>
                    {moduleStatsData.map((entry, index) => (
                      <Cell key={entry.name || index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Error & Success Ratio Trend */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Error & Success Ratio Trend</h3>
            <PeriodSelector value={errorSuccessRatioPeriod} onChange={setErrorSuccessRatioPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={errorSuccessRatioData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="successCount"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={{ fill: "#10b981", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Success"
                />
                <Line
                  type="monotone"
                  dataKey="errorCount"
                  stroke="#ef4444"
                  strokeWidth={2.5}
                  dot={{ fill: "#ef4444", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Error"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Error & Success Ratio */}
        {/* <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Error & Success Ratio</h3>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Success', value: overview?.totalApiCalls - overview?.totalFailedApiCalls || 0 },
                    { name: 'Error', value: overview?.totalFailedApiCalls || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardPage;
